// =============================================================================
// FASE 7B — Realtime (WebSocket) untuk V2. ADDITIVE, lapisan penyampaian data.
// - Auth Supabase (access_token dikirim saat handshake ?token=).
// - Feature flag: hanya connect bila realtimeEnabled (dari /api/v2/public-config).
// - FALLBACK AMAN: bila gagal/putus/ditolak -> app TETAP normal (halaman & data tetap).
// - TAHAN JARINGAN HP: reconnect backoff + reconnect saat tab kembali foreground +
//   reconnect otomatis saat access_token berubah (refresh Supabase).
// Provider ini TIDAK mengubah logika bisnis; hanya memicu refetch data halaman terkait.
// =============================================================================
import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useV2Auth } from "@/v2/V2AuthContext";

const Ctx = createContext({ status: "off", subscribe: () => () => {} });
export const useV2Realtime = () => useContext(Ctx);

export function V2RealtimeProvider({ children }) {
  const { session, realtimeEnabled } = useV2Auth();
  const token = session?.access_token || null;
  const [status, setStatus] = useState("off");
  const subs = useRef(new Set());
  const wsRef = useRef(null);
  const attempt = useRef(0);
  const stopped = useRef(false);
  const timer = useRef(null);
  const loggedPermanent = useRef(false);   // agar pesan "berhenti" dicatat SEKALI saja
  const MAX_ATTEMPTS = 5;                    // batasi percobaan reconnect (hemat baterai)

  const notify = useCallback((evt) => {
    subs.current.forEach((cb) => { try { cb(evt); } catch (e) { /* isolasi subscriber */ } });
  }, []);

  const subscribe = useCallback((cb) => {
    subs.current.add(cb);
    return () => subs.current.delete(cb);
  }, []);

  useEffect(() => {
    // Tidak connect bila flag OFF atau belum ada token -> app tetap normal (fetch saat buka halaman).
    if (!realtimeEnabled || !token) { setStatus("off"); return; }

    stopped.current = false;
    attempt.current = 0;
    loggedPermanent.current = false;
    const WS_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");

    const clearTimer = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };

    const stopPermanently = (msg) => {
      stopped.current = true;
      setStatus("offline");
      clearTimer();
      if (!loggedPermanent.current) {
        loggedPermanent.current = true;
        console.info(msg);   // INFO sekali saja (bukan error) -> tak membanjiri console
      }
    };

    const scheduleReconnect = () => {
      if (stopped.current) return;
      // Batas percobaan: setelah MAX_ATTEMPTS gagal -> berhenti (jangan reconnect selamanya).
      if (attempt.current >= MAX_ATTEMPTS) {
        stopPermanently("[Realtime] Berhenti menyambung ulang setelah beberapa percobaan gagal. Aplikasi tetap berjalan penuh tanpa realtime.");
        return;
      }
      attempt.current += 1;
      // Backoff 2s..30s (tahan jaringan HP; berhenti agresif agar hemat baterai).
      const delay = Math.min(1000 * 2 ** Math.min(attempt.current, 5), 30000);
      clearTimer();
      timer.current = setTimeout(connect, delay);
    };

    function connect() {
      if (stopped.current) return;
      setStatus("connecting");
      let ws;
      try {
        ws = new WebSocket(`${WS_BASE}/api/ws?token=${encodeURIComponent(token)}`);
      } catch (e) {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => { attempt.current = 0; setStatus("live"); };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg && msg.event === "v2_mutation") notify(msg);
        } catch (err) { /* abaikan pesan non-JSON */ }
      };
      ws.onclose = (ev) => {
        setStatus("offline");
        if (stopped.current) return;
        const code = ev?.code;
        if (code === 4403) {
          // PENOLAKAN PERMANEN: realtime dimatikan server (REALTIME_ENABLED=false).
          // Jangan reconnect lagi. Indikator tetap "Offline"; app tetap normal (fetch per halaman).
          stopPermanently("[Realtime] Dinonaktifkan oleh server (REALTIME_ENABLED=false). Berhenti menyambung; aplikasi tetap berjalan penuh tanpa realtime.");
          return;
        }
        if (code === 4401) {
          // Token kedaluwarsa/invalid: jangan hammer dgn token sama. Akan reconnect otomatis
          // saat token diperbarui (effect re-run karena `token` berubah).
          stopped.current = true;
          clearTimer();
          console.info("[Realtime] Token kedaluwarsa. Akan menyambung ulang setelah token diperbarui.");
          return;
        }
        // Putus jaringan biasa -> reconnect dengan backoff (dibatasi MAX_ATTEMPTS).
        scheduleReconnect();
      };
      ws.onerror = () => { try { ws.close(); } catch (e) { /* noop */ } };
    }

    connect();

    // HP: app di-background lalu dibuka -> segera reconnect bila koneksi mati.
    const onVis = () => {
      if (document.visibilityState === "visible" && wsRef.current?.readyState !== 1 && !stopped.current) {
        attempt.current = 0; connect();
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      stopped.current = true;
      clearTimer();
      document.removeEventListener("visibilitychange", onVis);
      try { wsRef.current?.close(); } catch (e) { /* noop */ }
    };
    // token berubah (refresh Supabase) -> reconnect dgn token baru. flag berubah -> connect/disconnect.
  }, [token, realtimeEnabled, notify]);

  return <Ctx.Provider value={{ status, subscribe }}>{children}</Ctx.Provider>;
}

// Hook halaman: panggil `cb` (di-debounce) saat ada kejadian relevan.
// `predicate(evt)` opsional: filter modul yang relevan utk halaman (default: semua).
export function useV2LiveRefresh(cb, { predicate, debounce = 800 } = {}) {
  const { subscribe } = useV2Realtime();
  const cbRef = useRef(cb); cbRef.current = cb;
  const predRef = useRef(predicate); predRef.current = predicate;
  const timer = useRef(null);
  useEffect(() => {
    const unsub = subscribe((evt) => {
      try { if (predRef.current && !predRef.current(evt)) return; } catch (e) { /* noop */ }
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => { try { cbRef.current && cbRef.current(); } catch (e) { /* noop */ } }, debounce);
    });
    return () => { unsub(); if (timer.current) clearTimeout(timer.current); };
  }, [subscribe]);
}
